// localStorage glue for the peek-readiness probe's synthetic signal
// override (introduced as L2-followup Part C-bugfix of
// docs/planning/firefly_role_context_presentation.md).
//
// Why this module exists: the probe's parent
// (`DevPanelActivityDockPeekStatsSection`) only renders while the
// user is on a mode that shows the DevPanel. Navigating away
// unmounts the probe, which would wipe the `useState`-backed
// override. Persisting to localStorage keeps the dev's last
// configuration across nav transitions and tab reloads.
//
// Kept smaller than `lib/activityDock/assistLevel.ts`: no subscriber
// set, no cross-tab `storage` event handler — the probe is mounted in
// exactly one place at a time, and the dev tool doesn't need
// multi-tab live sync. If a future surface needs the broadcast
// behavior, lift it here without changing the read/write API.

import type { KpiTone } from "@/components/common/kpi/kpiTypes";

export const PEEK_PROBE_OVERRIDE_STORAGE_KEY =
  "bb-dev-peek-probe-override-v1";

export type PeekProbeSignalOverride = {
  enabled: boolean;
  unsentCount: number;
  adsTone: KpiTone | null;
};

export const PEEK_PROBE_OVERRIDE_DEFAULT: PeekProbeSignalOverride = {
  enabled: false,
  unsentCount: 3,
  adsTone: "amber",
};

const KPI_TONES: readonly KpiTone[] = [
  "amber",
  "green",
  "gold",
  "red",
  "grey",
];

function parseAdsTone(value: unknown): KpiTone | null {
  if (value === null) return null;
  if (typeof value !== "string") return PEEK_PROBE_OVERRIDE_DEFAULT.adsTone;
  return KPI_TONES.includes(value as KpiTone)
    ? (value as KpiTone)
    : PEEK_PROBE_OVERRIDE_DEFAULT.adsTone;
}

export function parsePeekProbeOverride(value: unknown): PeekProbeSignalOverride {
  if (value == null || typeof value !== "object") {
    return PEEK_PROBE_OVERRIDE_DEFAULT;
  }
  const raw = value as Record<string, unknown>;
  const unsent = Number(raw.unsentCount);
  return {
    enabled: raw.enabled === true,
    unsentCount: Number.isFinite(unsent) && unsent >= 0 ? Math.floor(unsent) : 0,
    adsTone: parseAdsTone(raw.adsTone),
  };
}

export function loadPeekProbeOverride(): PeekProbeSignalOverride {
  if (typeof localStorage === "undefined") return PEEK_PROBE_OVERRIDE_DEFAULT;
  try {
    const raw = localStorage.getItem(PEEK_PROBE_OVERRIDE_STORAGE_KEY);
    if (raw === null) return PEEK_PROBE_OVERRIDE_DEFAULT;
    return parsePeekProbeOverride(JSON.parse(raw));
  } catch {
    return PEEK_PROBE_OVERRIDE_DEFAULT;
  }
}

export function savePeekProbeOverride(override: PeekProbeSignalOverride): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      PEEK_PROBE_OVERRIDE_STORAGE_KEY,
      JSON.stringify(override),
    );
  } catch {
    // Quota / private-mode: skip persistence. The in-memory state
    // still drives the probe for the current session.
  }
}
